// =============================================================================
// src/app/bus.ts — AppBus: pub/sub tipado para eventos cross-modulo.
//
// Proveniencia: substitui o acoplamento ad-hoc do mockup original (chamadas
//   diretas entre SectionManager, topbar nav e Copiloto em unified-app.js).
//   O contrato de eventos vive em AppEventMap (src/app/types.ts) — emitir/ouvir
//   sempre casa o nome do evento com a forma do payload em tempo de compilacao.
//
// SRP: este arquivo so faz roteamento de eventos. Sem estado de dominio.
// =============================================================================

import type { AppBus, AppEventMap } from './types';

/** Conjunto de handlers por nome de evento. `unknown` interno, tipado nas bordas. */
type HandlerSet = Set<(payload: unknown) => void>;

/**
 * Cria um AppBus isolado. Cada chamada produz um barramento independente — o
 * scaffold cria um unico via createAppContext, mas testes podem criar os seus.
 */
export function createAppBus(): AppBus {
  const registry = new Map<keyof AppEventMap, HandlerSet>();
  return {
    on: (event, handler) => subscribe(registry, event, handler),
    off: (event, handler) => unsubscribe(registry, event, handler),
    emit: (event, payload) => dispatch(registry, event, payload),
  };
}

/** Registra um handler e devolve a funcao de cancelamento (unsubscribe). */
function subscribe<K extends keyof AppEventMap>(
  registry: Map<keyof AppEventMap, HandlerSet>,
  event: K,
  handler: (payload: AppEventMap[K]) => void,
): () => void {
  const handlers = registry.get(event) ?? new Set();
  handlers.add(handler as (payload: unknown) => void);
  registry.set(event, handlers);
  return () => unsubscribe(registry, event, handler);
}

/** Remove um handler previamente registrado (no-op se ausente). */
function unsubscribe<K extends keyof AppEventMap>(
  registry: Map<keyof AppEventMap, HandlerSet>,
  event: K,
  handler: (payload: AppEventMap[K]) => void,
): void {
  registry.get(event)?.delete(handler as (payload: unknown) => void);
}

/** Entrega o payload a todos os handlers do evento, isolando excecoes por handler. */
function dispatch<K extends keyof AppEventMap>(
  registry: Map<keyof AppEventMap, HandlerSet>,
  event: K,
  payload: AppEventMap[K],
): void {
  const handlers = registry.get(event);
  if (!handlers) return;
  for (const handler of handlers) invokeHandler(handler, payload, event);
}

/** Chama um handler protegendo o loop de dispatch contra erro de um assinante. */
function invokeHandler(
  handler: (payload: unknown) => void,
  payload: unknown,
  event: keyof AppEventMap,
): void {
  try {
    handler(payload);
  } catch (cause) {
    // Provenience: um handler que estoura nao pode derrubar os demais assinantes.
    console.error(
      `[AppBus] handler do evento ${JSON.stringify(event)} lancou erro`,
      cause,
    );
  }
}
